import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { SafeIframe } from '../iframeEmbed';

const DEFAULT_SIZE = { width: 0, height: 0 }; // filled on open from viewport
const MIN_W = 720;
const MIN_H = 480;
const STORAGE_KEY = 'rcm_cross_app_modal_size';

function readStoredSize() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.width === 'number' &&
      typeof parsed.height === 'number' &&
      parsed.width >= MIN_W &&
      parsed.height >= MIN_H
    ) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function clampSize(width, height) {
  const maxW = Math.max(MIN_W, window.innerWidth - 24);
  const maxH = Math.max(MIN_H, window.innerHeight - 24);
  return {
    width: Math.min(maxW, Math.max(MIN_W, width)),
    height: Math.min(maxH, Math.max(MIN_H, height)),
  };
}

function defaultViewportSize() {
  // Slightly inset so backdrop is visible around the panel.
  const nested =
    typeof window !== 'undefined' &&
    (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();
  const wFactor = nested ? 0.9 : 0.92;
  const hFactor = nested ? 0.88 : 0.9;
  return clampSize(window.innerWidth * wFactor, window.innerHeight * hFactor);
}

/**
 * Overlay that embeds another RCM service URL in an iframe.
 * Large by default and user-resizable (drag bottom-right handle).
 * Forwards AUTH_DATA / ORG_CONTEXT when the child requests them.
 */
export default function CrossAppIframeModal({
  open,
  url,
  title = 'Details',
  onClose,
  organizationId,
}) {
  const iframeRef = useRef(null);
  const dragRef = useRef(null);
  const [maximized, setMaximized] = useState(false);
  const [size, setSize] = useState(() => {
    if (typeof window === 'undefined') return { width: 1200, height: 800 };
    return readStoredSize() || defaultViewportSize();
  });

  useEffect(() => {
    if (!open) return undefined;

    setSize((prev) => {
      const stored = readStoredSize();
      if (stored) return clampSize(stored.width, stored.height);
      if (prev.width > 0 && prev.height > 0) return clampSize(prev.width, prev.height);
      return defaultViewportSize();
    });
    setMaximized(false);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onResize = () => {
      setSize((prev) => clampSize(prev.width, prev.height));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || maximized) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(size));
    } catch {
      /* ignore */
    }
  }, [open, size, maximized]);

  useEffect(() => {
    if (!open) return undefined;

    const replyWithAuth = (target) => {
      if (!target) return;
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const org =
        organizationId ||
        localStorage.getItem('selectedOrganizationId') ||
        localStorage.getItem('organizationId') ||
        localStorage.getItem('organization_id');
      if (token || org) {
        target.postMessage(
          {
            type: 'AUTH_DATA',
            authToken: token || undefined,
            token: token || undefined,
            refreshToken: refreshToken || undefined,
            refresh_token: refreshToken || undefined,
            organizationId: org || undefined,
            organization_id: org || undefined,
          },
          '*'
        );
      }
      if (org) {
        target.postMessage(
          {
            type: 'ORGANIZATION_CONTEXT',
            organizationId: org,
            organization_id: org,
            authToken: token || undefined,
            token: token || undefined,
            refreshToken: refreshToken || undefined,
            refresh_token: refreshToken || undefined,
          },
          '*'
        );
      }
    };

    const onMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'REQUEST_AUTH_DATA' && data.type !== 'REQUEST_ORG_CONTEXT') return;
      const frameWin = iframeRef.current?.contentWindow;
      if (frameWin && event.source === frameWin) {
        replyWithAuth(frameWin);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, organizationId]);

  const startResize = useCallback(
    (e) => {
      if (maximized) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = size.width;
      const startH = size.height;
      dragRef.current = { startX, startY, startW, startH };

      const onMove = (ev) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        setSize(
          clampSize(dragRef.current.startW + dx, dragRef.current.startH + dy)
        );
      };
      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [maximized, size.height, size.width]
  );

  if (!open || !url) return null;

  const panelStyle = maximized
    ? {
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxWidth: '100vw',
        maxHeight: '100vh',
      }
    : {
        width: size.width,
        height: size.height,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100vh - 16px)',
      };

  return (
    <div
      className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/60 p-2 sm:p-3"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="relative flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <h2 className="truncate text-lg font-semibold text-gray-900">{title}</h2>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMaximized((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              title={maximized ? 'Restore size' : 'Maximize'}
              aria-label={maximized ? 'Restore size' : 'Maximize'}
            >
              {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {maximized ? 'Restore' : 'Maximize'}
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              title="Open in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title="Close"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 bg-gray-50">
          <SafeIframe
            iframeRef={iframeRef}
            src={url}
            title={title}
            serviceName={title}
            onLoad={() => {
              try {
                const win = iframeRef.current?.contentWindow;
                if (!win) return;
                const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
                const org =
                  organizationId ||
                  localStorage.getItem('selectedOrganizationId') ||
                  localStorage.getItem('organizationId') ||
                  localStorage.getItem('organization_id');
                if (token || org) {
                  win.postMessage(
                    {
                      type: 'AUTH_DATA',
                      authToken: token || undefined,
                      token: token || undefined,
                      organizationId: org || undefined,
                      organization_id: org || undefined,
                    },
                    '*'
                  );
                }
              } catch {
                /* cross-origin */
              }
            }}
          />
        </div>

        {!maximized && (
          <button
            type="button"
            aria-label="Resize dialog"
            title="Drag to resize"
            onMouseDown={startResize}
            className="absolute bottom-1 right-1 z-10 flex h-5 w-5 cursor-nwse-resize items-end justify-end rounded-sm border border-gray-300 bg-white/90 p-0.5 shadow-sm hover:bg-gray-50"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M10 2 L2 10 M10 6 L6 10 M10 10 L10 10" stroke="#6b7280" strokeWidth="1.5" fill="none" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
